import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import {
  getUserPreferences,
  updateUserCookiePreferences,
  CookiePreferences
} from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loading";

const CURRENT_VERSION = "1.0";

const defaultPreferences: CookiePreferences = {
  consent_status: "managed",
  version: CURRENT_VERSION,
  timestamp: new Date().toISOString(),
  categories: {
    strictly_necessary: true,
    analytics: false,
    marketing: false
  }
};

export function CookieManagement() {
  const { t } = useTranslation("common");
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    async function loadPreferences() {
      if (!profile || !profile.id) return;

      setLoading(true);
      try {
        const data = await getUserPreferences(profile.id, profile.role);
        if (data?.preferences?.cookies) {
          setPreferences(data.preferences.cookies);
        }
      } catch (error) {
        console.error("Error loading cookie preferences:", error);
      } finally {
        setLoading(false);
      }
    }

    loadPreferences();
  }, [profile]);

  const handleSave = async () => {
    if (!profile || !profile.id) return;

    setLoading(true);
    try {
      const updatedPreferences: CookiePreferences = {
        ...preferences,
        consent_status: "managed",
        timestamp: new Date().toISOString()
      };

      await updateUserCookiePreferences(
        profile.id,
        profile.role,
        updatedPreferences
      );
      setPreferences(updatedPreferences);

      toast({
        title: t("status.success"),
        description: t("cookies.management.success")
      });
    } catch (error) {
      console.error("Error saving cookie preferences:", error);
      toast({
        title: t("status.error"),
        description: t("cookies.management.error"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="container max-w-4xl py-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("cookies.management.title")}
        </h1>
        <p className="text-muted-foreground mt-2">
          {t("cookies.management.description")}
        </p>
      </div>

      <div className="grid gap-6">
        {/* Strictly Necessary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold">
                {t("cookies.management.categories.strictly_necessary.title")}
              </CardTitle>
              <CardDescription>
                {t(
                  "cookies.management.categories.strictly_necessary.description"
                )}
              </CardDescription>
            </div>
            <Switch checked={true} disabled aria-readonly />
          </CardHeader>
        </Card>

        {/* Analytics */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold">
                {t("cookies.management.categories.analytics.title")}
              </CardTitle>
              <CardDescription>
                {t("cookies.management.categories.analytics.description")}
              </CardDescription>
            </div>
            <Switch
              checked={preferences.categories.analytics}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({
                  ...prev,
                  categories: { ...prev.categories, analytics: checked }
                }))
              }
            />
          </CardHeader>
        </Card>

        {/* Marketing */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold">
                {t("cookies.management.categories.marketing.title")}
              </CardTitle>
              <CardDescription>
                {t("cookies.management.categories.marketing.description")}
              </CardDescription>
            </div>
            <Switch
              checked={preferences.categories.marketing}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({
                  ...prev,
                  categories: { ...prev.categories, marketing: checked }
                }))
              }
            />
          </CardHeader>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave}>{t("cookies.management.save")}</Button>
      </div>
    </div>
  );
}
